import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const RD = () => {
  const { t } = useTranslation();
  const [activeProduct, setActiveProduct] = useState(0);

  // Auto-rotate products every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveProduct((prev) => (prev + 1) % 2);
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  const rndProducts = [
    {
      id: 1,
      name: t('rd.product.uavFighter.name'),
      tagline: t('rd.product.uavFighter.tagline'),
      description: t('rd.product.uavFighter.description'),
      image: '/assets/uavfighter1.png',
      specs: {
        '📦 Payload Capacity': '1.5 Tons',
        '✈️ Cruise Speed': '0.6 Mach',
        '🚀 Maximum Speed': '0.9 Mach',
        '🎯 Combat Radius': '500 nm',
        '🛫 Operational Altitude': '25,000 Feet',
        '⏱️ Endurance': '4 Hours',
        '⚡ Engine Type': 'Turbofan with Afterburner',
        '📡 Sensor Suite': 'Multi-Spectral Targeting System',
        '🛡️ Stealth Rating': 'Ultra-Low Observable',
        '🔄 Maneuverability': '9G Capable',
        '📶 Communication': 'Satellite & LOS'
      },
      features: [
        t('rd.product.uavFighter.features.1'),
        t('rd.product.uavFighter.features.2'),
        t('rd.product.uavFighter.features.3'),
        t('rd.product.uavFighter.features.4')
      ]
    },
    {
      id: 2,
      name: t('rd.product.cruiseMissile.name'),
      tagline: t('rd.product.cruiseMissile.tagline'),
      description: t('rd.product.cruiseMissile.description'),
      image: '/assets/miniintellegentcruisemissile1.png',
      specs: {
        '🛫 Service Ceiling': '15,000 feet',
        '⚡ Operational Altitude': '12,000 feet',
        '📶 Communication Range': '70 km',
        '📏 Operational Range': '150 km',
        '⏱️ Endurance': '30 Minutes',
        '✈️ Cruise Speed': '160 KIAS',
        '🚀 Maximum Speed': '200 KIAS',
        '💨 Max. Dive Speed': '400 KIAS',
        '⚖️ MTOW': '40 kg',
        '📦 Payload Capacity': '5 kg',
        '🚁 Launching': 'Air Platform Launched',
        '🔧 Power Plant': 'Jet Engine',
        '📷 Payload ISR': 'AI Assisted EO Camera',
        '🎯 Terminal Guidance': 'EO or GNSS',
        '📐 Wingspan': '1.14 m',
        '📏 Length': '1.73 m',
        '📏 Height': '0.4 m'
      },
      features: [
        t('rd.product.cruiseMissile.features.1'),
        t('rd.product.cruiseMissile.features.2'),
        t('rd.product.cruiseMissile.features.3'),
        t('rd.product.cruiseMissile.features.4')
      ]
    }
  ];

  return (
    <div className="pt-16 md:pt-20">
      {/* Military Hero Section */}
      <section className="section-military bg-black">
        <div className="container-military text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-black tracking-tighter mb-8 text-gradient-military"
          >
            {t('rd.page.title')}
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="w-32 h-1 bg-gradient-to-r from-military-accent to-military-accent mx-auto"
          />
        </div>
      </section>

      {/* Products Showcase Section */}
      <section className="section-military bg-gray-900">
        <div className="container-military">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-gradient-military mb-6">
              {t('rd.products.title')}
            </h2>
            <p className="text-lg md:text-xl text-military-silver max-w-3xl mx-auto font-military-mono">
              {t('rd.products.subtitle')}
            </p>
          </motion.div>

          <div className="space-y-16">
            {rndProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                {/* Product Image */}
                <div className={`relative ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="relative h-[500px] rounded-lg overflow-hidden military-card">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>

                {/* Product Details */}
                <div className={`${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                  <h3 className="text-3xl md:text-4xl font-black tracking-tighter text-gradient-military mb-4">
                    {product.name}
                  </h3>
                  <p className="text-xl md:text-2xl text-military-accent font-bold mb-6">
                    {product.tagline}
                  </p>
                  <p className="text-lg md:text-xl text-military-silver mb-8 leading-relaxed font-military-mono">
                    {product.description}
                  </p>
                  
                  {/* Technical Specifications */}
                  <div>
                    <h4 className="text-xl md:text-2xl font-black tracking-tighter text-gradient-military mb-6">
                      {t('rd.specs.title')}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(product.specs).map(([key, value]) => (
                        <div key={key} className="military-card p-4">
                          <div className="flex justify-between items-center">
                            <span className="font-bold text-military-accent font-military-mono text-sm">
                              {key}
                            </span>
                            <span className="font-black text-gradient-military text-sm">
                              {value}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Innovation Highlights */}
      <section className="section-military bg-black">
        <div className="container-military">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-gradient-military mb-6">
              {t('rd.innovation.title')}
            </h2>
            <p className="text-lg md:text-xl text-military-silver max-w-3xl mx-auto font-military-mono">
              {t('rd.innovation.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="military-card text-center p-6 md:p-8"
            >
              <div className="w-16 h-16 bg-military-accent rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🤖</span>
              </div>
              <h3 className="text-xl md:text-2xl font-black tracking-tighter text-gradient-military mb-4">
                {t('rd.innovation.ai.title')}
              </h3>
              <p className="text-military-silver font-military-mono text-sm md:text-base">
                {t('rd.innovation.ai.description')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="military-card text-center p-6 md:p-8"
            >
              <div className="w-16 h-16 bg-military-accent rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🛡️</span>
              </div>
              <h3 className="text-xl md:text-2xl font-black tracking-tighter text-gradient-military mb-4">
                {t('rd.innovation.stealth.title')}
              </h3>
              <p className="text-military-silver font-military-mono text-sm md:text-base">
                {t('rd.innovation.stealth.description')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="military-card text-center p-6 md:p-8"
            >
              <div className="w-16 h-16 bg-military-accent rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-xl md:text-2xl font-black tracking-tighter text-gradient-military mb-4">
                {t('rd.innovation.propulsion.title')}
              </h3>
              <p className="text-military-silver font-military-mono text-sm md:text-base">
                {t('rd.innovation.propulsion.description')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="section-military bg-gray-900">
        <div className="container-military text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-gradient-military mb-8">
              {t('rd.cta.title')}
            </h2>
            <p className="text-lg md:text-xl text-military-silver mb-12 max-w-2xl mx-auto font-military-mono">
              {t('rd.cta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a href="/contact" className="btn-military-primary">
                {t('rd.cta.contact')}
              </a>
              <a href="/contact" className="btn-military-secondary">
                {t('rd.cta.partnerships')}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RD;
